import React from 'react';
import { Modal, Field } from '../../components/ui';
import { money } from '../../utils/helpers';
import { RATE_TYPES, SERVICE_STATUSES, OTHER_SERVICE_TYPE, isPerUnit } from './useServices';
import '../../styles/money.css';

// Desktop add/edit service dialog — a fixed, purposeful width; every line of
// fields sized to what goes in it (who · owner · charges · when). The footer
// keeps the live total / balance in view. Enter in a box saves.
export default function ServiceFormDesktop({ vm }) {
  const {
    form, setF, editing, saving, save, setOpen, typeOptions, functionOptions, userOptions,
    vendorMode, setVendorMode, selectedType,
  } = vm;
  const perUnit = isPerUnit(form.RateType);
  const unitWord = form.RateType === 'PerPlate' ? 'Plates confirmed' : form.RateType === 'PerDay' ? 'Days' : form.RateType === 'PerHour' ? 'Hours' : 'Quantity';
  const total = Number(form.TotalAmount || 0);
  const advance = editing ? 0 : Number(form.AdvancePaid || 0);

  // Enter in a plain box saves; the time boxes handle Enter themselves (they
  // commit on blur and mark the event handled), selects / textarea are left alone.
  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || e.defaultPrevented || saving) return;
    const t = e.target;
    if (t.tagName !== 'INPUT' || t.type === 'checkbox' || t.getAttribute('list')) return;
    e.preventDefault();
    save();
  };

  return (
    <Modal
      dense className="mn-svc-modal"
      title={editing ? `Edit ${editing.ServiceType}` : 'Add Service'}
      onClose={() => setOpen(false)}
      footer={<>
        <span className="mn-sum">
          {total > 0
            ? <>Total <strong>{money(total)}</strong>
              {perUnit && Number(form.Rate) > 0 && Number(form.ConfirmedCount) > 0 && <> ({money(form.Rate)} × {form.ConfirmedCount})</>}
              {!editing && advance > 0 && <> · balance <strong className="due">{money(total - advance)}</strong></>}</>
            : (selectedType?.ItemLabel
              ? `Save, then open it to add the ${selectedType.ItemLabel.toLowerCase()} list and payments.`
              : 'Save, then open it to add its items and payments.')}
        </span>
        <button className="btn light" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
      </>}
    >
      <div onKeyDown={onKeyDown}>
        {/* What and for which function */}
        <div className="mn-line">
          <label className="field mn-w200">Service *
            <select autoFocus value={form.ServiceTypeId ?? ''} onChange={(e) => setF('ServiceTypeId', e.target.value)}>
              <option value="">- select -</option>
              {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          {form.ServiceTypeId === OTHER_SERVICE_TYPE && (
            <div className="mn-w180">
              <Field label="New service name" value={form.ServiceType} required onChange={(v) => setF('ServiceType', v)} />
            </div>
          )}
          <div className="mn-grow">
            <Field label="For function" type="select" value={form.FunctionId} options={functionOptions}
              onChange={(v) => setF('FunctionId', v)} />
          </div>
          <div className="mn-w130">
            <Field label="Status" type="select" value={form.Status} options={SERVICE_STATUSES} onChange={(v) => setF('Status', v)} />
          </div>
        </div>

        {/* Who — the owner is a user: an existing account, or one made from the
            mobile number so he can sign in and see his own work. */}
        <div className="mn-line">
          <div className="mn-grow">
            <Field label="Company name" value={form.CompanyName} onChange={(v) => setF('CompanyName', v)} />
          </div>
          <div className="mn-grow">
            <Field label="Owner / contact person" value={form.ContactName} onChange={(v) => setF('ContactName', v)} />
          </div>
        </div>
        <div className="mn-line">
          <div className="mn-seg-cell">
            <span className="mn-seg-label">Owner login</span>
            <div className="mn-seg">
              <button type="button" className={vendorMode === 'new' ? 'on' : ''} onClick={() => setVendorMode('new')}>By mobile number</button>
              <button type="button" className={vendorMode === 'existing' ? 'on' : ''} onClick={() => setVendorMode('existing')}>Existing user</button>
            </div>
          </div>
          {vendorMode === 'existing' ? (
            <div className="mn-grow">
              <Field label="User" type="select" value={form.VendorUserId} options={userOptions} onChange={(v) => setF('VendorUserId', v)} />
            </div>
          ) : (
            <label className="field mn-w200">Owner mobile (login, PIN 1234)
              <input type="tel" inputMode="numeric" maxLength={10} value={form.ContactMobile ?? ''} placeholder="10 digits"
                onChange={(e) => setF('ContactMobile', e.target.value)} />
            </label>
          )}
        </div>

        {/* Charges */}
        <div className="mn-line">
          <div className="mn-w150">
            <Field label="Rate type" type="select" value={form.RateType} options={RATE_TYPES} onChange={(v) => setF('RateType', v)} />
          </div>
          <div className="mn-w120 mn-amt">
            <Field label={perUnit ? 'Rate ₹ / unit' : 'Rate (₹)'} type="number" value={form.Rate} onChange={(v) => setF('Rate', v)} />
          </div>
          {perUnit && (
            <div className="mn-w120 mn-amt">
              <Field label={unitWord} type="number" value={form.ConfirmedCount} onChange={(v) => setF('ConfirmedCount', v)} />
            </div>
          )}
          <div className="mn-w130 mn-amt">
            <Field label="Total (₹)" type="number" value={form.TotalAmount} onChange={(v) => setF('TotalAmount', v)} />
          </div>
          {!editing && (
            <div className="mn-w130 mn-amt">
              <Field label="Advance now (₹)" type="number" value={form.AdvancePaid} onChange={(v) => setF('AdvancePaid', v)} />
            </div>
          )}
        </div>

        {/* When */}
        <div className="mn-line">
          <div className="mn-w150">
            <Field label="Date" type="date" required value={form.ServiceDate} onChange={(v) => setF('ServiceDate', v)} />
          </div>
          <div className="mn-w150">
            <Field label="Start time" type="time" value={form.StartTime} onChange={(v) => setF('StartTime', v)} />
          </div>
          <div className="mn-w150">
            <Field label="End time" type="time" value={form.EndTime} onChange={(v) => setF('EndTime', v)} />
          </div>
        </div>

        <Field label="Notes" type="textarea" rows={2} value={form.Notes} onChange={(v) => setF('Notes', v)} />
      </div>
    </Modal>
  );
}
